import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {Button} from 'reactstrap';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import BroadcastSmsApiService from '../../api/BroadcastSmsApiService';
import SettingsApiService from '../../api/SettingsApiService';
import PageShell from '../PageShell';
import {BROADCAST_TEMPLATE_OVERHEAD, estimateSegments} from '../../utilities/SmsSegments';

export default function SmsSubscribers(props) {
    const {isMobile, setSnackbarProperties} = props;

    const broadcastSmsApiService = new BroadcastSmsApiService();
    const settingsApiService = new SettingsApiService();
    const navigate = useNavigate();

    const [message, setMessage] = useState('');
    const [subscriberCount, setSubscriberCount] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [isSmsEnabled, setIsSmsEnabled] = useState(false);
    const [settingLoaded, setSettingLoaded] = useState(false);

    // Unlike email, every recipient of a text is billed, and a long message is billed more than once.
    // Showing both numbers before sending is the point.
    const segments = useMemo(
        () => (message.trim() ? estimateSegments(message.trim() + ' '.repeat(BROADCAST_TEMPLATE_OVERHEAD)) : 0),
        [message]
    );
    const totalSegments = segments * subscriberCount;

    useEffect(() => {
        if (!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);
            return;
        }

        if (!authenticationManager.isSuperAdmin()) {
            navigate('/');
            return;
        }

        document.title = 'Executive Storage Suites - Text Subscribers';

        void getSmsEnabled();
        void getSubscriberCount();
    }, []);

    async function getSmsEnabled() {
        setIsSmsEnabled(await settingsApiService.isSmsEnabled());
        setSettingLoaded(true);
    }

    async function getSubscriberCount() {
        const response = await broadcastSmsApiService.getReachableSubscriberCount();

        if (response?.success) {
            setSubscriberCount(response.data);
        } else {
            setSnackbarProperties(`Error getting subscriber count: ${response?.message}`, 'error');
        }
    }

    async function sendSms() {
        setSending(true);
        const response = await broadcastSmsApiService.sendToSubscribers(message);

        if (response?.success) {
            setSnackbarProperties(`Text queued for ${response.data} recipient${response.data === 1 ? '' : 's'}`, 'success');
            setMessage('');
        } else {
            setSnackbarProperties(`Error sending text: ${response?.message}`, 'error');
        }

        setSending(false);
        setConfirmOpen(false);
    }

    if (!settingLoaded) {
        return <Box />;
    }

    if (!isSmsEnabled) {
        return (
            <PageShell title={'Text Subscribers'} isMobile={isMobile} disableCard>
                <Alert severity={'info'} sx={{maxWidth: isMobile ? '100%' : '600px'}}>
                    Text messaging is switched off, so nothing sent from here would reach anyone. Turn it
                    on under Admin Settings once the texting account is set up.
                </Alert>
            </PageShell>
        );
    }

    return (
        <PageShell
            title={'Text Subscribers'}
            subtitle={`${subscriberCount} opted-in recipient${subscriberCount === 1 ? '' : 's'}`}
            isMobile={isMobile}
            disableCard
        >
            <Typography sx={{marginBottom: '10px'}}>
                This message will be sent to every subscriber who has opted in to text messages ({subscriberCount} recipient{subscriberCount === 1 ? '' : 's'}). Subscribers who have not opted in are not included and cannot be added here.
            </Typography>
            <Box sx={{maxWidth: isMobile ? '100%' : '600px'}}>
                <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label={'Message'}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    sx={{marginBottom: '10px'}}
                />
                <Typography variant={'body2'} sx={{marginBottom: '10px'}}>
                    {`Roughly ${segments} segment${segments === 1 ? '' : 's'} per message, ${totalSegments} in total. Your business name and "Reply STOP to opt out." are added automatically and are included in this estimate.`}
                </Typography>
                {segments > 1 && (
                    <Alert severity={'info'} sx={{marginBottom: '10px'}}>
                        Messages over one segment are billed per segment. Shortening the message reduces the cost.
                    </Alert>
                )}
                <Button
                    className={isMobile ? 'mobile-modal-button' : ''}
                    variant={'contained'}
                    disabled={!message.trim() || subscriberCount === 0}
                    onClick={() => setConfirmOpen(true)}
                >
                    Send Text
                </Button>
            </Box>
            <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)}>
                <DialogTitle>Send to all opted-in subscribers?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`This will text ${subscriberCount} subscriber${subscriberCount === 1 ? '' : 's'}, using about ${totalSegments} billed segment${totalSegments === 1 ? '' : 's'}. This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={sending} onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button disabled={sending} onClick={sendSms}>{sending ? 'Sending...' : 'Send'}</Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
}

SmsSubscribers.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};
